import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
} from '@mui/material';
import { RootState } from '../../store';
import { get, isFunction, reduce, throttle } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { DialogReducerType, hideDialogAction } from './VideoDialog.reducer';
import classNames from 'classnames';
import { trackCtaAction } from '../../Pages/Flo/Flo.reducers';
import styles from './VideoDialog.module.css';

export const dialogSelector =
	(floIndex: number) =>
	(state: RootState): DialogReducerType =>
		//@ts-ignore
		get(state, `videoDialog.${floIndex}`) as DialogReducerType;

const validProtocols = ['http', 'https', 'ftp', 'ssh', 'telnet', 'sftp', 'file', 'data'];

const pattern = new RegExp('^(' + validProtocols.join('|') + ')://');

const DialogComponent = ({
	isTabView,
	style,
	floId,
	floIndex,
}: {
	isTabView?: boolean;
	style?: CSSProperties;
	floId: string;
	floIndex: number;
}) => {
	const {
		visible,
		onConfirm,
		onCancel,
		header,
		body,
		confirmButtonText,
		confirmButtonType,
		cancelButtonText,
		showConfirm,
		showCancel,
		centerAlignFooter,
		headerClassName,
		classContainer,
		classDialogRoot,
		hideClosebutton,
		bodyClassName,
		classDialogConfirmButton,
		disablePortal,
		noBackdropClick,
		container,
		confirmButtonUrl,
		disableAutoFocus,
		cancelButtonClassName,
		fullScreenView,
		id,
		classContentWrapper,
		position,
		classes,
	} =
		useSelector(
			(state: RootState): DialogReducerType =>
				get(state, `videoDialog.${floIndex}`) as DialogReducerType
		) || {};
	const dispatch = useDispatch();
	const showAppComment = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.appCommentPosition.show`)
	);
	const wrapper = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails['wrapper']`, 'rounded')
	);
	const showWrapper = wrapper && wrapper !== 'rounded';

	const cardRef = useRef<HTMLDivElement>(null);
	const [cardDimensions, setCardDimensions] = useState({ width: 0, height: 0 });

	const [showDialog, setShowDialog] = useState(false);
	const [positionClasses, setPositionClasses] = useState<{ [key: string]: boolean }>({});

	useEffect(() => {
		let newPosition = position?.replace(/-center/g, '').split('-') || [];
		const newPositionClasses = reduce(
			newPosition,
			(acc, posVal: string) => ({ ...acc, [get(styles, posVal)]: true }),
			{ [styles.notCenter]: !!newPosition.length }
		);
		setPositionClasses(newPositionClasses);
	}, [position, setPositionClasses, id]);

	useEffect(() => {
		if (visible) {
			if (id === 'start') {
				setTimeout(() => {
					setShowDialog(true);
				}, 1000);
			} else {
				setShowDialog(true);
			}
		} else {
			setShowDialog(false);
		}
	}, [visible]);

	useEffect(() => {
		const dimensions = {
			height: cardRef.current?.offsetHeight || 0,
			width: cardRef.current?.offsetWidth || 0,
		};
		if (cardRef.current && dimensions.width && dimensions.height) {
			setCardDimensions(dimensions);
		}
	}, [
		cardRef.current,
		setCardDimensions,
		showDialog,
		id,
		cardRef.current?.offsetHeight,
		cardRef.current?.offsetWidth,
	]);

	const handleClose = useCallback(
		(event?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
			let keepDialog = false;
			if (noBackdropClick && reason === 'backdropClick') return;
			if (isFunction(onCancel)) {
				keepDialog = onCancel(undefined, !reason || reason === 'backdropClick');
			}
			if (!keepDialog) {
				dispatch(
					hideDialogAction({
						data: '',
						floId,
						floIndex,
					})
				);
			}
			if (id && reason) {
				dispatch(
					trackCtaAction({
						id,
						skip: true,
						buttonProperties: [],
						floId,
						floIndex,
					})
				);
			}
		},
		[dispatch, onCancel, noBackdropClick, floId]
	);

	const handleConfirm = useCallback(
		throttle(
			() => {
				let keepDialog = false;
				if (isFunction(onConfirm)) {
					keepDialog = onConfirm(dispatch);
				}
				if (!keepDialog) {
					dispatch(
						hideDialogAction({
							data: '',
							floId,
							floIndex,
						})
					);
				}
				if (id) {
					dispatch(
						trackCtaAction({
							id,
							skip: false,
							buttonProperties: [
								{
									key: 'buttonName',
									id: confirmButtonUrl,
								},
							],
							floId,
							floIndex,
						})
					);
				}
			},
			1000,
			{ leading: true, trailing: false }
		),
		[dispatch, onConfirm, floId]
	);
	let link = confirmButtonUrl;
	if (link === '#' || link === '/#') {
		link = '';
	}
	if (link && !pattern.test(link)) {
		link = `//${link}`;
	}
	if (!showDialog || showAppComment) return <span />;
	return (
		<Dialog
			open={visible}
			style={style}
			disableEscapeKeyDown
			scroll={'paper'}
			disablePortal={disablePortal}
			disableAutoFocus={disableAutoFocus}
			container={container}
			classes={{
				paper: classNames(styles.container, classContainer),
				root: classNames(styles.dialogRoot, classDialogRoot, {
					[styles.fullScreenView]: fullScreenView,
					[styles.withWrapper]: showWrapper,
				}),
			}}
			onClose={handleClose}
		>
			{!hideClosebutton && (
				<IconButton
					className={classNames(styles.closeButton, classes?.closeButton)}
					aria-label="close"
					onClick={handleClose}
					onTouchEnd={handleClose}
					sx={{
						position: 'absolute',
						right: 24,
						top: 24,
					}}
				>
					<CloseIcon className={classNames(styles.closeIcon, classes?.closeIcon)} />
				</IconButton>
			)}
			<div
				className={classNames(
					styles.contentWrapper,
					classContentWrapper,
					positionClasses
				)}
				ref={cardRef}
			>
				{(header || !hideClosebutton) && (
					<DialogTitle
						id="responsive-dialog-title"
						className={classNames(styles.title, headerClassName)}
					>
						{header}
					</DialogTitle>
				)}
				<DialogContent className={styles.dialogContent}>
					<DialogContentText className={classNames(styles.dialogMessage, bodyClassName)}>
						{body}
					</DialogContentText>
				</DialogContent>
				<div className={styles.actionsWrapper}>
					{(showCancel || showConfirm) && (
						<DialogActions
							classes={{
								root: classNames(styles.actions, {
									[styles.center]: centerAlignFooter,
								}),
							}}
						>
							{showCancel && (
								<Button
									autoFocus={!disableAutoFocus}
									disableFocusRipple
									disableElevation
									onClick={handleClose}
									className={classNames(cancelButtonClassName)}
								>
									{cancelButtonText}
								</Button>
							)}
							{showConfirm && (
								<Button
									component={link ? 'a' : 'button'}
									href={link}
									target={'_blank'}
									disableFocusRipple
									disableElevation
									variant="contained"
									className={classNames(styles.confirmButton, classDialogConfirmButton, {
										[styles.deleteButton]: confirmButtonType === 'delete',
									})}
									onClick={handleConfirm}
									autoFocus={!disableAutoFocus}
								>
									{confirmButtonText}
								</Button>
							)}
						</DialogActions>
					)}
				</div>
			</div>
			<div
				className={classNames(styles.animatingBg, positionClasses)}
				style={{
					width: `${cardDimensions.width}px`,
					height: `${cardDimensions.height}px`,
				}}
			></div>
		</Dialog>
	);
};

export default DialogComponent;
